<script setup>
defineProps({
  items: {
    type: Array,
    default: () => [],
  },
});

defineEmits(["view", "edit"]);

const formatCurrency = (value) => {
  return new Intl.NumberFormat("th-TH", {
    style: "currency",
    currency: "THB",
    minimumFractionDigits: 0,
  }).format(value || 0);
};

const formatDate = (value) => {
  if (!value) return "-";
  return new Intl.DateTimeFormat("th-TH", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(value));
};

const methodText = (method) => {
  const map = {
    cash: "เงินสด",
    bank_transfer: "โอนธนาคาร",
    qr_payment: "QR Payment",
    credit_card: "บัตรเครดิต",
  };
  return map[method] || method;
};

const statusText = (status) => {
  const map = {
    completed: "รับชำระสมบูรณ์",
    partial: "ชำระบางส่วน",
    pending_verify: "รอตรวจสอบ",
  };
  return map[status] || status;
};

const statusBadgeClass = (status) => {
  const map = {
    completed: "text-bg-success",
    partial: "text-bg-warning",
    pending_verify: "text-bg-secondary",
  };
  return map[status] || "text-bg-light";
};

const escapeCSV = (value) => {
  const str = String(value ?? "");
  return `"${str.replace(/"/g, '""')}"`;
};

const exportCSV = (items) => {
  if (!items.length) return;

  const headers = [
    "เลขที่รับชำระ",
    "ใบแจ้งหนี้",
    "วันที่ชำระ",
    "ลูกบ้าน",
    "ยูนิต",
    "อาคาร",
    "ช่องทาง",
    "จำนวนเงิน",
    "สถานะ",
    "เลขที่ใบเสร็จ",
    "บันทึกโดย",
  ];

  const rows = items.map((payment) => [
    payment.paymentNo,
    payment.invoiceNo,
    formatDate(payment.paymentDate),
    payment.residentName,
    payment.unitCode,
    payment.building,
    methodText(payment.method),
    payment.amount || 0,
    statusText(payment.status),
    payment.receiptNo,
    payment.recordedBy,
  ]);

  const csv = [
    headers.map(escapeCSV).join(","),
    ...rows.map((row) => row.map(escapeCSV).join(",")),
  ].join("\n");

  const blob = new Blob(["\uFEFF" + csv], {
    type: "text/csv;charset=utf-8;",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const today = new Date().toISOString().slice(0, 10);

  link.href = url;
  link.download = `payments-${today}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
</script>
<template>
  <div class="card border-0 shadow-sm rounded-4">
    <div class="card-body p-0">
      <div
        class="px-4 pt-4 pb-3 d-flex justify-content-between align-items-center flex-wrap gap-2"
      >
        <div>
          <h5 class="fw-bold mb-1">รายการรับชำระเงิน</h5>
          <div class="text-muted small">
            พบทั้งหมด {{ items.length }} รายการ
          </div>
        </div>

        <button
          type="button"
          class="btn btn-outline-danger border-danger-subtle rounded-3 shadow-sm"
          :disabled="!items.length"
          @click="exportCSV(items)"
        >
          <i class="bi bi-download me-1"></i>Export
        </button>
      </div>

      <div class="table-responsive">
        <table class="table align-middle mb-0">
          <thead class="table-light">
            <tr>
              <th class="ps-4">เลขที่รับชำระ</th>
              <th>ใบแจ้งหนี้</th>
              <th>วันที่ชำระ</th>
              <th>ลูกบ้าน</th>
              <th>ยูนิต</th>
              <th>ช่องทาง</th>
              <th class="text-end">จำนวนเงิน</th>
              <th>สถานะ</th>
              <th>ใบเสร็จ</th>
              <th class="text-center pe-4">จัดการ</th>
            </tr>
          </thead>

          <tbody v-if="items.length">
            <tr v-for="payment in items" :key="payment.id">
              <td class="ps-4">
                <div class="fw-semibold">{{ payment.paymentNo }}</div>
                <div class="small text-muted">{{ payment.recordedBy }}</div>
              </td>
              <td>{{ payment.invoiceNo }}</td>
              <td>{{ formatDate(payment.paymentDate) }}</td>
              <td>{{ payment.residentName }}</td>
              <td>
                <div>{{ payment.unitCode }}</div>
                <div class="small text-muted">{{ payment.building }}</div>
              </td>
              <td>
                <span class="badge rounded-pill text-bg-light border text-dark">
                  {{ methodText(payment.method) }}
                </span>
              </td>
              <td class="text-end fw-semibold">
                {{ formatCurrency(payment.amount) }}
              </td>
              <td>
                <span
                  class="badge rounded-pill"
                  :class="statusBadgeClass(payment.status)"
                >
                  {{ statusText(payment.status) }}
                </span>
              </td>
              <td>{{ payment.receiptNo }}</td>
              <td class="text-center pe-4">
                <div class="btn-group">
                  <button
                    type="button"
                    class="btn btn-sm btn-light border"
                    title="ดูรายละเอียด"
                    @click="$emit('view', payment)"
                  >
                    <i class="bi bi-eye text-primary-emphasis"></i>
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm btn-light border"
                    title="แก้ไข"
                    @click="$emit('edit', payment)"
                  >
                    <i class="bi bi-pencil-square text-warning-emphasis"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>

          <tbody v-else>
            <tr>
              <td colspan="10" class="text-center py-5">
                <div class="text-muted">
                  <i class="bi bi-cash-stack fs-2 d-block mb-2"></i>
                  ไม่พบข้อมูลการรับชำระตามเงื่อนไขที่ค้นหา
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<style scoped>
.table th {
  white-space: nowrap;
  font-size: 0.9rem;
}
</style>
